import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../network/ApiClient';
import type {
  RoundEndPayload, RematchSnapshot, RematchStatePayload,
  NextRoundReadyPayload, RoundEndExitedPayload, OrdinaryReadyStatus,
} from '../types/roundEnd';
import type { RoundReadyResponse } from '../types/game';

/** RoundReadyResponse.status values that confirm the backend actually
 *  recorded this viewer's readiness. A resolved promise alone is NOT proof —
 *  'no_round' / 'session_over' / 'not_participant' resolve successfully but
 *  mean the press had no effect. 'already_advanced' is a benign no-op
 *  success: the round moved on (via others' readiness or the deadline)
 *  before this request was processed. */
const READY_CONFIRMED_STATUSES: ReadonlySet<RoundReadyResponse['status']> = new Set([
  'advancing', 'waiting', 'already_advanced',
]);

/**
 * Manages the round-end overlay lifecycle.
 *
 * - Mounts when GameScene dispatches 'phaser:round-end'.
 * - For an ORDINARY round (sessionOver false), 'phaser:next-round-ready'
 *   begins the VISUAL exit handshake: the payload and ready state are
 *   RETAINED (not cleared) and `exiting` flips true, so the consuming
 *   OrdinaryRoundEndOverlay can set GameplayBackdrop.active=false and let its
 *   canonical ~180ms exit run with the finished board still visible. Only
 *   when the overlay itself calls `onOrdinaryExited` (wired to
 *   GameplayBackdrop's onExited) does this hook dispatch
 *   'react:round-end-exited' with the exact pending gameId and clear
 *   payload/ready/exiting state.
 * - For the FINAL-MATCH path (sessionOver true) or a reconnect jump-ahead (no
 *   payload was ever shown), 'phaser:next-round-ready' clears the payload
 *   IMMEDIATELY, exactly as before — never waits for GameplayBackdrop, never
 *   dispatches the ordinary acknowledgment.
 * - markReady() posts readiness to the backend at most once per round
 *   (single-flight), tracks it through an explicit OrdinaryReadyStatus state
 *   machine (idle/pending/confirmed/failed) derived from the ACTUAL response
 *   contract (not merely "the promise resolved"), and discards a late
 *   result if a new round began, the overlay started exiting, or the hook
 *   unmounted in the meantime.
 * - navigateHome() dispatches 'phaser:navigate-home' for session-over path.
 *
 * Does NOT create any WebSocket subscriptions. All network state lives in Phaser.
 */
export function useRoundEndOverlay() {
  const [payload, setPayload] = useState<RoundEndPayload | null>(null);
  // Mirror of `payload` for the once-registered event handlers below: lets
  // onNextRoundReady decide on the CURRENT payload without a stale closure and
  // without side effects inside a state updater (StrictMode double-invokes
  // updaters, which would double-dispatch the ack).
  const payloadRef = useRef<RoundEndPayload | null>(null);

  const [readyStatus, setReadyStatus] = useState<OrdinaryReadyStatus>('idle');
  // True once next-round-ready has been received for the CURRENT ordinary
  // payload — the overlay should begin GameplayBackdrop's exit while payload
  // content stays mounted until onOrdinaryExited runs.
  const [exiting, setExiting] = useState(false);

  // calledRef is reset on each new phaser:round-end so the guard is per-round;
  // also reset to false on a 'failed' outcome so the user can retry.
  const calledRef = useRef(false);
  // Single-flight guard for the final-match rematch vote (per final screen).
  const votedRef  = useRef(false);
  // Set in this effect's cleanup — guards every async continuation below
  // against updating state after the hook has unmounted.
  const unmountedRef = useRef(false);
  // Bumped whenever an in-flight markReady() result must be treated as stale:
  // a new round begins, the ordinary exit begins, the exit completes, or the
  // hook unmounts. A markReady() continuation compares its captured value
  // against the current one before applying its result.
  const readyGenRef = useRef(0);
  // The next game id announced by the ordinary next-round-ready event,
  // consumed exactly once by onOrdinaryExited. null = nothing pending (never
  // armed, or already consumed) — a stray/duplicate/stale call is a no-op.
  const pendingExitGameIdRef = useRef<number | null>(null);

  useEffect(() => {
    const onRoundEnd = (e: Event) => {
      const detail = (e as CustomEvent<RoundEndPayload>).detail;
      calledRef.current = false;
      votedRef.current  = false;
      readyGenRef.current++;
      pendingExitGameIdRef.current = null;
      setExiting(false);
      // Restored-ready: reconnect state already proves this viewer's seat is
      // in the authoritative ready set — enter 'confirmed' directly and guard
      // against a duplicate POST, without ever calling the API.
      const restored = !detail.sessionOver && (detail.readyPositions ?? []).includes(detail.myPosition);
      if (restored) calledRef.current = true;
      setReadyStatus(restored ? 'confirmed' : 'idle');
      payloadRef.current = detail;
      setPayload(detail);
    };

    const onNextRoundReady = (e: Event) => {
      const detail  = (e as CustomEvent<NextRoundReadyPayload>).detail;
      const current = payloadRef.current;

      if (current && !current.sessionOver && typeof detail?.gameId === 'number') {
        // Ordinary path: begin the visual exit handshake. Payload and ready
        // state are RETAINED — GameplayBackdrop.onExited (wired by the
        // overlay to onOrdinaryExited) completes the transition below.
        readyGenRef.current++; // invalidate any in-flight markReady() result
        pendingExitGameIdRef.current = detail.gameId;
        setExiting(true);
        return;
      }

      // Final-match / reconnect-jump-ahead path: unchanged immediate clear.
      // Never waits for GameplayBackdrop, never dispatches the ordinary ack.
      payloadRef.current = null;
      setPayload(null);
      setExiting(false);
      pendingExitGameIdRef.current = null;
    };

    // Live rematch vote updates — merged into the current payload so the
    // overlay re-renders yes/no counts from ONE authoritative source.
    const onRematchState = (e: Event) => {
      const rematch = (e as CustomEvent<RematchStatePayload>).detail?.rematch;
      if (!rematch) return;
      payloadRef.current = payloadRef.current ? { ...payloadRef.current, rematch } : payloadRef.current;
      setPayload((prev) => (prev ? { ...prev, rematch } : prev));
    };

    window.addEventListener('phaser:round-end',       onRoundEnd);
    window.addEventListener('phaser:next-round-ready', onNextRoundReady);
    window.addEventListener('phaser:rematch-state',    onRematchState);
    return () => {
      unmountedRef.current = true;
      window.removeEventListener('phaser:round-end',       onRoundEnd);
      window.removeEventListener('phaser:next-round-ready', onNextRoundReady);
      window.removeEventListener('phaser:rematch-state',    onRematchState);
    };
  }, []);

  /**
   * Fired by OrdinaryRoundEndOverlay when GameplayBackdrop's onExited
   * completes the ordinary exit animation. Dispatches the acknowledgment
   * with the EXACT pending gameId from the triggering next-round-ready
   * event, then clears payload/ready/exiting state. Guarded so a stray call,
   * a duplicate call, or a call after unmount is always a safe no-op — and
   * because the pending id is consumed (set to null) on first use, it can
   * never acknowledge a later, different pending transition either.
   */
  const onOrdinaryExited = useCallback(() => {
    if (unmountedRef.current) return;
    const gameId = pendingExitGameIdRef.current;
    if (gameId === null) return; // nothing pending — already consumed, or never armed
    pendingExitGameIdRef.current = null;
    window.dispatchEvent(
      new CustomEvent<RoundEndExitedPayload>('react:round-end-exited', { detail: { gameId } }),
    );
    payloadRef.current = null;
    setPayload(null);
    setExiting(false);
    setReadyStatus('idle');
    readyGenRef.current++;
  }, []);

  /**
   * Mark THIS player ready for the next round. Single-flight per round (the
   * calledRef guard), but UNLIKE a fire-and-forget request this tracks the
   * real outcome via readyStatus: 'pending' while in flight, 'confirmed'
   * only when the response's `status` field actually confirms the backend
   * recorded it, 'failed' otherwise (including a rejected promise) — which
   * re-enables the button for a retry. Never creates or navigates to the
   * next game itself — the transition arrives via next_game_created (handled
   * by Phaser's SessionChannel) once the server gate opens.
   */
  const markReady = useCallback(async (sessionId: number) => {
    if (calledRef.current) return;
    calledRef.current = true;
    const gen = readyGenRef.current;
    setReadyStatus('pending');
    try {
      const res = await apiClient.markRoundReady(sessionId);
      // Stale: a new round began, the overlay started exiting, or the hook
      // unmounted while this request was in flight — never touch state for a
      // round/overlay this result no longer belongs to.
      if (unmountedRef.current || gen !== readyGenRef.current) return;
      const confirmed = READY_CONFIRMED_STATUSES.has(res.status);
      setReadyStatus(confirmed ? 'confirmed' : 'failed');
      if (!confirmed) calledRef.current = false; // allow retry
    } catch (err) {
      console.error('[useRoundEndOverlay] markRoundReady failed — the server deadline will still advance the round; the client transitions on next_game_created:', err);
      if (unmountedRef.current || gen !== readyGenRef.current) return;
      setReadyStatus('failed');
      calledRef.current = false; // allow retry — marking ready is idempotent server-side
    }
  }, []);

  /** Dispatch phaser:navigate-home so GameScene can tear down and go to MenuScene. */
  const navigateHome = useCallback(() => {
    window.dispatchEvent(new CustomEvent('phaser:navigate-home'));
  }, []);

  /**
   * Submit the final-match rematch vote exactly once (single-flight; the
   * server is idempotent regardless). Never navigates by itself: the response
   * snapshot is funneled to GameScene ('phaser:rematch-snapshot'), which is
   * the ONE resolution actor for both REST responses and WS broadcasts.
   */
  const submitRematchVote = useCallback(async (sessionId: number, vote: 'yes' | 'no') => {
    if (votedRef.current) return;
    votedRef.current = true;
    try {
      const res = await apiClient.submitRematchVote(sessionId, vote);
      if (res.rematch) {
        window.dispatchEvent(new CustomEvent<{ rematch: RematchSnapshot }>('phaser:rematch-snapshot', {
          detail: { rematch: res.rematch as RematchSnapshot },
        }));
      }
    } catch (err) {
      // Allow a retry after a transient failure — the backend vote is
      // first-vote-immutable, so a retried submit can never double-count.
      // Same-tick double-clicks are still collapsed (votedRef is set
      // synchronously before the await).
      votedRef.current = false;
      console.error('[useRoundEndOverlay] submitRematchVote failed — retry allowed:', err);
    }
  }, []);

  return {
    payload, markReady, navigateHome, submitRematchVote,
    readyStatus, exiting, onOrdinaryExited,
  };
}
